import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, ElementRef, OnInit, signal, viewChild, viewChildren } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { BiHttpErrorResponse } from "@globals/models/BiHttpErrorResponse";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

import { getDateFormatByCountry, getLanguageCodeByCountryId, getLanguageCodeByHostName, isInsideIFrameOrPopup, isStringNullOrEmpty } from "@globals/helper-functions";
import { AngularResizeEventModule, ResizedEvent } from "angular-resize-event";
import moment from "moment-timezone";
import { RadioButtonModule } from "primeng/radiobutton";
import { catchError, finalize, forkJoin, of, switchMap, take, tap } from "rxjs";

import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { BiTranslateDirective, BiTranslatePipe, BiTranslateService } from "@globals/bi-translate";
import { BiAccordionComponent } from "@globals/components/bi-accordion/bi-accordion.component";
import { BiAccordionModule } from "@globals/components/bi-accordion/bi-accordion.module";
import { BiSpinnerModule } from "@globals/components/spinner/bi-spinner.module";
import { DriftstatusWebMessagesModel } from "@globals/openapi-models/model/driftstatusWebMessagesModel";
import { WebMessage } from "@globals/openapi-models/model/webMessage";
import { WebMessageType } from "@globals/openapi-models/model/webMessageType";
import { WebmessageModuleProfileModel } from "@globals/openapi-models/model/webmessageModuleProfileModel";
import { BiDatePipe } from "@globals/pipes/bi-date.pipe";
import { GlobalPipesModule } from "@globals/pipes/global-pipes.module";
import { convertWindowsTimeZoneToIANA } from "@globals/timezoneTypeConversion";
import { BadgeModule } from "primeng/badge";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { PanelModule } from "primeng/panel";
import { IframeModuleUrlParamsHandlerService } from "../core/iframe-module-url-params-handler.service";
import { WebModuleService } from "../core/web-module.service";
import { DriftstatusUrlParams } from "./DriftstatusUrlParams";

@UntilDestroy()
@Component({
  selector: "iframe-driftstatus",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BiTranslatePipe,
    BiTranslateDirective,
    AngularResizeEventModule,
    ButtonModule,
    BiDatePipe,
    BiSpinnerModule,
    PanelModule,
    GlobalPipesModule,
    BiAccordionModule,
    CardModule,
    RadioButtonModule,
    BadgeModule
  ],
  providers: [WebModuleService],
  templateUrl: "./iframe-driftstatus.component.html",
  styleUrls: ["./iframe-driftstatus.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: "iframe-component"
  }
})
export default class IframeDriftstatusComponent implements OnInit {
  public title = signal<string>("");

  /**
   * Model with relevant settings as well the web messages
   */
  private driftstatusWebModel: DriftstatusWebMessagesModel;

  /**
   * Model where messages include those from archive
   */
  private driftstatusWebModelArchive: DriftstatusWebMessagesModel;

  /**
   * The model currently used for display. We use this as we can shift between archive and non-archive and wanna have data cached.
   * ONLY relevant if customer is setup to be able to see archived messages
   */
  public currentWebMessagesModel = signal<DriftstatusWebMessagesModel>(undefined);
  public noMessagesToShow = computed(() => !this.currentWebMessagesModel() || !this.currentWebMessagesModel()?.profilesWithWebmessages?.some(pWm => pWm.webMessages.length > 0));
  public urlParams = signal<DriftstatusUrlParams>(undefined);

  public dateTimeFormat = signal<string>(undefined);

  public messageCount = signal<number>(0);

  /**
   * Whether the list should show messages from archive or not. Default false.
   */
  protected readonly showFromArchive = signal(false);

  public loading = signal(true);
  public customCssLoaded = signal(false);
  public initialized = computed(() => this.currentWebMessagesModel() !== undefined && this.customCssLoaded() && !this.loading());
  private heightSet = false;
  public titleStyle = computed(() => {
    let style = "";
    if (this.urlParams()) {
      if (this.urlParams().titleColor) style += `color:  ${this.urlParams().titleColor}; `;
      if (this.urlParams().titleSize) style += "font-size: " + this.urlParams().titleSize + "px; ";
      if (this.urlParams().titleFont || this.urlParams().fontFamily) style += "font-family: " + (this.urlParams().titleFont ?? this.urlParams().fontFamily) + "; ";
    }
    return style;
  });
  public titleIconStyle = computed(() => {
    let style = "";
    if (this.urlParams()) {
      if (this.urlParams().titleSize) style += "font-size: " + this.urlParams().titleSize + "px; ";
      if (this.urlParams().titleColor) style += `color:  ${this.urlParams().titleColor}; `;
    }
    return style;
  });

  public readonly wrapperDiv = viewChild<ElementRef>("wrapperDiv");

  accordions = viewChildren<BiAccordionComponent>("accordion");

  constructor(
    private readonly webModuleService: WebModuleService,
    private readonly cd: ChangeDetectorRef,
    private readonly translator: BiTranslateService,
    private readonly router: Router,
    private readonly activeRoute: ActivatedRoute,
    public readonly sanitizer: DomSanitizer,
    private readonly urlParamsHandler: IframeModuleUrlParamsHandlerService
  ) {
    effect(() => {
      if (this.accordions()) {
        if (this.urlParams().fontFamily || this.urlParams().fontSize || this.urlParams().fontColor) {
          const bodyTextElements = document.querySelectorAll(".drift-body-text, .drift-message-title");

          bodyTextElements.forEach((el: HTMLElement) => {
            if (!isStringNullOrEmpty(this.urlParams().fontFamily)) {
              el.style.fontFamily = this.urlParams().fontFamily;

              if (el instanceof HTMLElement) {
                el.childNodes.forEach(childEl => {
                  if (childEl instanceof HTMLElement) {
                    childEl.style.fontFamily = this.urlParams().fontFamily;
                  }
                });
              }
            }

            if (this.urlParams().fontSize) el.style.fontSize = this.urlParams().fontSize + "px";
            if (this.urlParams().fontColor) el.style.color = this.urlParams().fontColor;
          });
        }

        if (this.urlParams().titleCaps) document.querySelectorAll(".drift-message-title").forEach((el: HTMLElement) => (el.style.textTransform = "uppercase"));

        if (this.urlParams().mainColor) document.querySelectorAll(".webmessage-card .p-card, .p-panel-content").forEach((el: HTMLElement) => (el.style.backgroundColor = this.urlParams().mainColor));
      }
    });
  }

  public ngOnInit() {
    this.activeRoute.queryParams
      .pipe(
        untilDestroyed(this),
        take(1),
        switchMap(params => {
          this.urlParams.set(params as DriftstatusUrlParams);

          this.urlParamsHandler.correctUrlColorParams(this.urlParams());

          if (!this.urlParams().customerId && !this.urlParams().customerIds) {
            this.loading.set(false);
            return this.translator.use(getLanguageCodeByHostName());
          }

          const observables = [this.getDriftstatusWebMessagesModel()];
          if (this.urlParams().cssUrl) observables.push(this.urlParamsHandler.loadAndProcessCustomCssUrlParam(this.urlParams().cssUrl));
          return forkJoin(observables).pipe(tap(() => this.customCssLoaded.set(true)));
        })
      )
      .subscribe(() => {
        // Use timeout here to ensure that the DOM is ready before we try to set the styles
        setTimeout(() => this.processUrlParams(), 100);
      });

    // As this component is intended to live inside an iFrame, we must ensure that we listen to the reload event
    // so this feature is possible.
    const messageEventlistener = (e: MessageEvent) => {
      if (e.data === "reload") window.location.reload();
    };

    window.removeEventListener("message", messageEventlistener);
    window.addEventListener("message", messageEventlistener);
  }

  /**
   * Fired when content height changes. Then we must post message to parent window so it can update the iFrame's height.
   * This is only relevant if this component is iFramed
   */
  public onContentHeightChanged() {
    if (isInsideIFrameOrPopup() && window.parent) {
      setTimeout(() => {
        window.parent.postMessage(
          {
            type: "HeightChanged",
            height: (this.wrapperDiv().nativeElement as HTMLDivElement).offsetHeight + 20
          },
          "*"
        );
      }, 200);
    }
  }

  public onShowActiveOrInActiveChanged(showInactive: boolean) {
    this.getDriftstatusWebMessagesModel(showInactive)
      .pipe(untilDestroyed(this), take(1))
      .subscribe(() => this.onContentHeightChanged());
  }

  private processUrlParams() {
    this.urlParamsHandler.processDriftStatusModuleUrlParams(this.urlParams());

    if (!this.urlParams().headerFont && this.urlParams().fontFamily) {
      this.urlParams.set({ ...this.urlParams(), headerFont: this.urlParams().fontFamily });
    }

    this.onContentHeightChanged();
  }

  public getDriftstatusWebMessagesModel(fromArchive?: boolean) {
    // Check if there's cached data
    if (fromArchive && this.driftstatusWebModelArchive) {
      this.currentWebMessagesModel.set(this.driftstatusWebModelArchive);
      return of(this.currentWebMessagesModel());
    } else if (!fromArchive && this.driftstatusWebModel) {
      this.currentWebMessagesModel.set(this.driftstatusWebModel);
      return of(this.currentWebMessagesModel());
    }

    const fromUtc = fromArchive ? moment().subtract(this.currentWebMessagesModel().numOfDaysBackArchive, "d").startOf("day").utc().format() : undefined; // Default 2 months back in archive
    const toUtc = fromArchive ? moment().utc().format() : undefined;

    const onlyInternal = this.urlParams().internalOnly;

    const customerIds = this.urlParams().customerIds ? this.urlParams().customerIds.split(",") : [this.urlParams().customerId];
    const profileIds = this.urlParams().profileId
      ? [this.urlParams().profileId]
      : this.urlParams().profileIds
        ? this.urlParams()
            .profileIds.split(",")
            .map(id => Number(id))
        : undefined;
    return this.webModuleService
      .getDriftstatusWebMessagesModel(
        customerIds,
        this.urlParams(),
        profileIds,
        fromUtc,
        toUtc,
        onlyInternal ? WebMessageType.Sms2Internals : WebMessageType.Sms2Webs,
        fromArchive,
        this.urlParams().completedAsActive
      )
      .pipe(
        finalize(() => this.loading.set(false)),
        tap(model => {
          this.translator.use(getLanguageCodeByCountryId(model.countryId));
          // Cache the data
          if (!fromArchive) this.driftstatusWebModel = model;
          else this.driftstatusWebModelArchive = model;

          this.title.set(!this.urlParams().header ? model.titleForWebModule : this.urlParams().header);
          if (!this.urlParams().permaLinkURL) {
            // Set default time zone so that date conversion from utc will be correct
            moment.tz.setDefault(convertWindowsTimeZoneToIANA(model.timezoneId));
            this.dateTimeFormat.set(getDateFormatByCountry(model.countryId) + " HH:mm");

            if (this.urlParams().displayAllContent && this.urlParams().hideProfiles) {
              // We need to merge all the webmessages for each profile into 1 ProfileWebmessages object. The only
              // relevant property is the "webMessages" - title isn't used and neither is "hasCriticalMessages" in this case.
              const allProfileMessageInOne: WebmessageModuleProfileModel = {
                title: "",
                webMessages: [],
                hasCriticalMessages: true,
                sortOrder: "1"
              };

              model.profilesWithWebmessages.forEach(pWm => {
                allProfileMessageInOne.webMessages.push(...pWm.webMessages);
              });
              model.profilesWithWebmessages = [allProfileMessageInOne];

              // remember to sort the list correctly again
              this.sortWebMessages(model.profilesWithWebmessages[0].webMessages);
            }

            if (this.urlParams().internalOnly) {
              // Convert the list of profile web messages to a new where the messages are all of type internal
              model.profilesWithWebmessages = model.profilesWithWebmessages.reduce((resultArray, currentValue) => {
                const profileWebmessages: WebmessageModuleProfileModel = {
                  title: currentValue.title,
                  webMessages: [],
                  hasCriticalMessages: false,
                  sortOrder: currentValue.sortOrder
                };
                // Get only the internal messages. We must use the striung value of enum when filtering the array
                const internals = currentValue.webMessages.filter(m => m.typeId.toString() === WebMessageType[WebMessageType.Sms2Internals]);
                if (internals.length > 0) {
                  profileWebmessages.webMessages = internals;
                  profileWebmessages.hasCriticalMessages = internals.findIndex(webMsg => webMsg.criticalStatus == true) !== -1;
                }
                resultArray.push(profileWebmessages);
                return resultArray;
              }, [] as Array<WebmessageModuleProfileModel>);
            }

            model.profilesWithWebmessages.forEach(pWm => this.sortWebMessages(pWm.webMessages, this.urlParams().reversedOrder));
          } else {
            this.messageCount.set(0);
            // find out if any webmessages are critical
            for (let i = 0; i < model.profilesWithWebmessages.length; i++) {
              this.messageCount.update(count => count + model.profilesWithWebmessages[i].webMessages.length);
            }
          }
          this.currentWebMessagesModel.set(model);

          this.cd.markForCheck();
        }),
        catchError(err => this.handleServerError(err))
      );
  }

  public onWrapperDivResized(event: ResizedEvent) {
    if (isInsideIFrameOrPopup() && window.parent && event.newRect.height > (event.oldRect?.height || 0) && !this.heightSet) {
      setTimeout(() => {
        window.parent.postMessage(
          {
            type: "HeightChanged",
            // Set height using element depending on whether grouped messages or all messages are displayed at once
            height: event.newRect.height
          },
          "*"
        );
        this.heightSet = true;
      }, 300);
    }
  }

  /**
   * Sorts a list of webmessages using the following rules:
   * - Critical messages ALWAYS comes first.
   * - The lower the id, the older the message is. So we first sort by critical status and then by the id.
   * NOTE: modifies the org. array and therefore doesn't return anything
   * @param reversed If true, sorts by lowest id (after critical status)
   */
  private sortWebMessages(messages: Array<WebMessage>, reversed?: boolean) {
    messages.sort((msg1, msg2) => {
      if (msg1.criticalStatus === msg2.criticalStatus) {
        if (reversed) return msg1.id < msg2.id ? -1 : 1;
        else return msg1.id > msg2.id ? -1 : 1;
      }

      if (msg1.criticalStatus) return -1;
      if (msg2.criticalStatus) return 1;

      return 0;
    });
  }

  private handleServerError(err: BiHttpErrorResponse) {
    // if not allowed, show to user by navigating to the unavailable page!
    if (err.status === 403 && err.error?.errorMessage.indexOf("customer") > -1)
      return this.router.navigate(["unavailable"], {
        state: { message: this.translator.instant("iFrame.ModuleNotActive") }
      });
    else throw err;
  }
}
